import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';

import { ReactComponent as ReportDownIcon } from 'assets/icons/report_down.svg';
import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';

import CustomSelect from 'components/CustomSelect';
import PurchaseRequest from './modals/PurchaseRequest';
import AcceptanceMaterials from './modals/AcceptanceMaterials';
import MinLevel from './modals/MinLevel';
import Pagination from 'components/Pagination';
import FilterSearch from 'components/FilterSearch';
import Movements from './modals/Movements';
import FilterDropdown from 'components/FilterDropdown';

import { GET, httpOk } from 'helpers/api';
import { checkPermission, DOWNLOAD_FILE, formatMoney } from 'helpers/helpers';

function Warehouse() {
	const { t } = useTranslation()
	const statuses = [
		{
			id: 'critical',
			name: t('critical')
		},
		{
			id: 'attention',
			name: t('attention')
		},
		{
			id: 'ok',
			name: 'Ок'
		},
	];

	const [dropdown, setDropdown] = useState(false)
	const [purchaseRequestModal, setPurchaseRequestModal] = useState({ show: false, item: null })
	const [movementsModal, setMovementsModal] = useState({ show: false, id: '' })
	const [minLevelModal, setMinLevelModal] = useState({ show: false, id: '' })
	const [acceptanceMaterialsModal, setAcceptanceMaterialsModal] = useState(false)


	const initFilterData = {
		page: 1,
		page_size: 20,
		search: '',
		status: '',
	}

	const [filterData, setFilterData] = useState(initFilterData)

	const [data, setData] = useState({})
	const [rawMaterials, setRawMaterials] = useState([])

	async function exportFile() {
		await DOWNLOAD_FILE('/inventory/raw-warehouse/items/export/', filterData, t('raw_material_warehouse'))
	}

	const handlePaginate = (args) => {
		setFilterData(prev => ({
			...prev,
			page: args.page + 1,
			page_size: args.page_size
		}));
	};

	const getData = useCallback(async () => {
		const [response, responseStats] = await Promise.all([
			GET('/inventory/raw-warehouse/dashboard/', filterData),
			GET('/inventory/raw-warehouse/items/', filterData),
		]);

		if (httpOk(response)) {
			setData({ ...response.data, ...responseStats.data, });
		}
	}, [filterData]);

	useEffect(() => {
		getData();
	}, [getData]);

	const getRawMaterials = useCallback(async () => {
		const response = await GET('/catalogs/raw-materials/?warehouse_type=raw');

		if (httpOk(response)) {
			setRawMaterials(response.data);
		}
	}, []); // Пустой массив зависимостей значит, что функция создастся один раз

	useEffect(() => {
		getRawMaterials();
	}, [getRawMaterials]);


	return (
		<>
			<div className="d-between">
				<div>
					<div className="main-content-title">
						{t('raw_material_warehouse')}
					</div>

					<div className="main-content-subtitle">
						{t('inventory_and_purchase_management')}
					</div>
				</div>

				<div className="d-flex gap-8">
					<button className="white gap-16" onClick={() => setPurchaseRequestModal({ show: true })}>
						<ReportDownIcon />
						{t('purchase_request')}
					</button>

					{/* <button className="dark gap-16" onClick={() => setAcceptanceMaterialsModal(true)}>
						<PlusIcon />
						{t('raw_material_receipt')}
					</button> */}
				</div>
			</div>

			<div className="d-flex gap-16">
				<div className="card w-100">
					<div className="d-between">
						<div>
							<div className="title">
								{t('warehouse_positions_count')}
							</div>
							<div className="value">
								{data?.summary?.total_items}
							</div>
						</div>
					</div>
				</div>

				<div className="card w-100">
					<div className="d-between">
						<div>
							<div className="title">
								{t('critical_stock')}
							</div>
							<div className="value text-danger">
								{data?.summary?.critical_count}
							</div>
						</div>
					</div>
				</div>

				<div className="card w-100">
					<div className="d-between">
						<div>
							<div className="title">
								{t('require_attention')}
							</div>
							<div className="value text-warning">
								{data?.summary?.attention_count}
							</div>
						</div>
					</div>
				</div>

				{checkPermission("prices.view.full") &&
					<div className="card w-100">
						<div className="d-between">
							<div>
								<div className="title">
									{t('total_value')}
								</div>
								<div className="value">
									{formatMoney(data?.summary?.total_value)} сум
								</div>
							</div>
						</div>
					</div>
				}
			</div>

			<div className="card">
				<div className="d-flex gap-16 w-100">
					<FilterSearch
						value={filterData.search}
						onChange={(e) => setFilterData({ ...filterData, search: e.target.value })}
						placeholder={t('search_by_name_or_code')}
					/>

					<CustomSelect
						options={statuses}
						value={statuses.find(option => option.id === filterData?.status) || ''}
						onChange={(option) => setFilterData({ ...filterData, status: option?.id })}
						getOptionLabel={(option) => option.name}
						getOptionValue={(option) => option.id}
						placeholder={t('select_status')}
					/>

					<FilterDropdown dropdown={dropdown} setDropdown={setDropdown}>
						<div className="form-group">
							<label htmlFor="name">{t('raw_material')}</label>
							<CustomSelect
								options={rawMaterials}
								value={rawMaterials.find(option => option.id === filterData?.raw_material_id) || ''}
								onChange={(option) => setFilterData({ ...filterData, raw_material_id: option?.id })}
								getOptionLabel={(option) => option.name}
								getOptionValue={(option) => option.id}
							/>
						</div>
					</FilterDropdown>
					<button className="white third" onClick={() => exportFile()}>
						<DownloadIcon />
						{t('export')}
					</button>
				</div>
			</div>

			<div className="card">
				<div className="d-between mb-30">
					<div className="fz16 text-third">
						{t('raw_materials_list')}
					</div>
				</div>

				<table className="table">
					<thead>
						<tr>
							<th style={{ width: '10%' }}>{t('category')}</th>
							<th style={{ width: '15%' }}>{t('name')}</th>
							<th style={{ width: '10%' }}>{t('code')}</th>
							<th style={{ width: '5%' }}>{t('balance')}</th>
							<th style={{ width: '10%' }}>{t('unit_short')}</th>
							{checkPermission("prices.view.full") &&
								<th style={{ width: '15%' }}>{t('average_cost')}</th>
							}
							<th style={{ width: '15%' }}>{t('min_balance')}</th>
							<th style={{ width: '10%' }}>{t('status')}</th>
							<th style={{ width: '10%' }}>{t('actions')}</th>
						</tr>
					</thead>

					<tbody>
						{data?.results?.map((item, index) => (
							<tr key={index}>
								<td>{item.category?.name}</td>
								<td>{item.raw_material_name}</td>
								<td>{item.raw_material_code}</td>
								<td>{formatMoney(item.available_quantity)}</td>
								<td>{item.unit_short_name}</td>
								{checkPermission("prices.view.full") &&
									<td>{formatMoney(item.avg_cost)} сум</td>
								}
								<td className="cursor" onClick={() => setMinLevelModal({ show: true, id: item.id })}>
									{formatMoney(item.min_level)}
								</td>
								<td>
									<div className={`alert alert-${item.status} w-fit-content`}>
										{t(item.status)}
									</div>
								</td>
								<td>
									<div className="d-flex align-items-center gap-8">
										<button className="transparent small" onClick={() => setMovementsModal({ show: true, id: item.id })}>
											{t('movements')}
										</button>

										<button className="white small" onClick={() => setPurchaseRequestModal({ show: true, item })}>
											{t('request')}
										</button>
									</div>
								</td>
							</tr>
						))}
					</tbody>
				</table>


				<Pagination
					paginate={handlePaginate}
					pagination={{
						total: data?.count || 0
					}}
				/>

				<PurchaseRequest modal={purchaseRequestModal.show} setModal={() => setPurchaseRequestModal({ show: false })} rawMaterial={purchaseRequestModal.item} />
				<AcceptanceMaterials modal={acceptanceMaterialsModal} setModal={setAcceptanceMaterialsModal} getData={getData} />
				<Movements modal={movementsModal.show} setModal={() => setMovementsModal({ show: false })} id={movementsModal.id} />
				<MinLevel modal={minLevelModal.show} setModal={() => setMinLevelModal({ show: false })} id={minLevelModal.id} getData={getData} />
			</div>
		</>
	)
}

export default Warehouse